<template>
  <section class="pst" dir="rtl" aria-live="polite">

    <!-- ===== טעינה ראשונה ===== -->
    <div v-if="loading && !stats" class="pst-loading">
      <v-skeleton-loader type="heading" class="mb-4" />
      <v-skeleton-loader type="image" height="14" class="mb-6" />
      <v-skeleton-loader type="list-item-two-line, list-item-two-line, list-item-two-line" />
    </div>

    <!-- ===== שגיאה ===== -->
    <div v-else-if="error && !stats" class="pst-error">
      <p>לא הצלחנו לטעון את מצב החבילות. בדקו את החיבור ונסו שוב.</p>
      <v-btn color="#0d2c6d" dark depressed @click="load">נסו שוב</v-btn>
    </div>

    <template v-else-if="stats">
      <v-progress-linear v-if="loading" indeterminate color="#0d2c6d" height="2" class="pst-bar" />

      <!-- ===== הכותרת: המצב במשפט ===== -->
      <header class="pst-head">
        <div>
          <p class="pst-headline">{{ headline }}</p>
          <p v-if="t.paid_no_hok" class="pst-risk">
            <v-icon small color="#c2410c">mdi-alert-circle-outline</v-icon>
            עוד {{ money(t.revenue_at_risk) }} בחודש לא נגבים: {{ count(t.paid_no_hok) }} מערכות בתשלום בלי הוראת קבע
          </p>
        </div>
        <div class="pst-updated">
          <span>עודכן ב-{{ updatedAt }}</span>
          <v-btn icon small :disabled="loading" aria-label="רענון" title="רענון" @click="load"><v-icon small>mdi-refresh</v-icon></v-btn>
        </div>
      </header>

      <!-- ===== ההרכב: פס אחד ===== -->
      <div class="pst-mix" role="img" :aria-label="mixLabel">
        <span v-for="seg in mix" :key="seg.key" class="pst-mix__seg" :class="'is-' + seg.key" :style="{ flexGrow: seg.value }"></span>
      </div>
      <ul class="pst-legend">
        <li v-for="seg in mix" :key="seg.key">
          <i class="pst-dot" :class="'is-' + seg.key"></i>
          {{ seg.label }}
          <b>{{ count(seg.value) }}</b>
          <span class="pst-muted">{{ pct(seg.value) }}</span>
        </li>
      </ul>

      <!-- ===== מספרים מרכזיים (ההכנסות - רק עם הרשאה מלאה) ===== -->
      <dl class="pst-figures" :style="{ gridTemplateColumns: 'repeat(' + figures.length + ', minmax(0, 1fr))' }">
        <div v-for="fig in figures" :key="fig.key" class="pst-fig">
          <dt>{{ fig.label }}</dt>
          <dd>{{ fig.value }}</dd>
          <span class="pst-muted">{{ fig.note }}</span>
        </div>
      </dl>

      <!-- ===== דורש טיפול ===== -->
      <section class="pst-block" aria-labelledby="pst-attention">
        <h2 id="pst-attention" class="pst-h">דורש טיפול</h2>
        <p v-if="!attention.length" class="pst-empty">
          <v-icon small color="#2e9e5b">mdi-check-circle-outline</v-icon>
          אין כרגע מערכות שדורשות טיפול.
        </p>
        <div v-for="item in attention" :key="item.key" class="pst-att" :class="'is-' + item.tone">
          <button type="button" class="pst-att__row" :aria-expanded="isOpen(item.key) ? 'true' : 'false'" @click="toggle(item.key)">
            <span class="pst-att__count">{{ count(item.count) }}</span>
            <span class="pst-att__text">
              <b>{{ item.title }}</b>
              <span class="pst-muted">{{ item.hint }}</span>
            </span>
            <v-icon class="pst-att__chev" :class="{ 'is-open': isOpen(item.key) }">mdi-chevron-down</v-icon>
          </button>
          <div v-if="isOpen(item.key)" class="pst-att__list">
            <div class="pst-scroll">
              <table class="pst-mini">
                <thead>
                  <tr><th>מספר מערכת</th><th>טלפון הבעלים</th><th>{{ item.detailHeader }}</th></tr>
                </thead>
                <tbody>
                  <tr v-for="row in item.list" :key="row.line_number">
                    <td class="pst-num">{{ row.line_number }}</td>
                    <td class="pst-num">{{ row.owner_phone || '—' }}</td>
                    <td>{{ item.detail(row) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="item.count > item.list.length" class="pst-muted pst-note">מוצגות {{ count(item.list.length) }} מתוך {{ count(item.count) }}</p>
          </div>
        </div>
      </section>

      <!-- ===== לפי חבילה ===== -->
      <section class="pst-block" aria-labelledby="pst-plans">
        <h2 id="pst-plans" class="pst-h">לפי חבילה</h2>
        <div class="pst-scroll">
          <table class="pst-table">
            <thead>
              <tr><th>חבילה</th><th>מערכות</th><th>עם הוראת קבע</th><th v-if="fullAccess">הכנסה חודשית</th></tr>
            </thead>
            <tbody>
              <tr v-for="row in stats.by_plan" :key="row.plan_id || row.name">
                <td>
                  <div class="pst-plan">
                    <span class="pst-plan__name">{{ row.name }}</span>
                    <span class="pst-tag" :class="'is-' + row.type">{{ typeLabel(row.type) }}</span>
                    <span v-if="row.agent" class="pst-tag is-agent">סוכן: {{ row.agent }}</span>
                  </div>
                  <span v-if="row.type === 'paid'" class="pst-muted">{{ money(row.price) }} לחודש</span>
                </td>
                <td>
                  <div class="pst-share">
                    <span class="pst-num">{{ count(row.systems) }}</span>
                    <span class="pst-share__track"><span class="pst-share__fill" :class="'is-' + row.type" :style="{ width: shareWidth(row.systems) }"></span></span>
                  </div>
                </td>
                <td class="pst-num">
                  <template v-if="row.type === 'paid'">
                    {{ count(row.with_hok) }}
                    <span v-if="row.with_hok < row.systems" class="pst-gap">חסרות {{ count(row.systems - row.with_hok) }}</span>
                  </template>
                  <span v-else class="pst-muted">—</span>
                </td>
                <td v-if="fullAccess" class="pst-num">{{ row.type === 'paid' ? money(row.revenue) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ===== 30 הימים האחרונים ===== -->
      <section class="pst-block pst-split">
        <div>
          <h2 class="pst-h">מאיפה הגיעו המערכות החדשות</h2>
          <p class="pst-muted pst-sub">נפתחו ב-30 הימים האחרונים</p>
          <ul class="pst-bars">
            <li v-for="row in newSystemsRows" :key="row.key">
              <span class="pst-bars__label">{{ row.label }}</span>
              <span class="pst-bars__track"><span class="pst-bars__fill" :style="{ width: barWidth(row.value, newSystemsMax) }"></span></span>
              <span class="pst-num">{{ count(row.value) }}</span>
            </li>
          </ul>
        </div>
        <div>
          <h2 class="pst-h">מעברי חבילה</h2>
          <p class="pst-muted pst-sub">ב-30 הימים האחרונים, לפי דרך המעבר</p>
          <ul class="pst-changes">
            <li v-for="row in changesRows" :key="row.key">
              <span class="pst-bars__label">{{ row.label }}</span>
              <span v-if="row.paid" class="pst-chip is-paid">לחבילה בתשלום {{ count(row.paid) }}</span>
              <span v-if="row.free" class="pst-chip is-free">לחינמית {{ count(row.free) }}</span>
            </li>
            <li v-if="!changesRows.length" class="pst-muted">לא היו מעברי חבילה בחודש האחרון.</li>
          </ul>
        </div>
      </section>

      <!-- ===== לפי סוכן ===== -->
      <section v-if="agentRows.length" class="pst-block" aria-labelledby="pst-agents">
        <h2 id="pst-agents" class="pst-h">לפי סוכן</h2>
        <div class="pst-scroll">
          <table class="pst-table">
            <thead>
              <tr><th>סוכן</th><th>מערכות בתשלום</th><th v-if="fullAccess">הכנסה חודשית</th></tr>
            </thead>
            <tbody>
              <tr v-for="row in agentRows" :key="row.line_number || 'none'">
                <td>{{ row.agent }} <span v-if="row.line_number" class="pst-muted pst-num">{{ row.line_number }}</span></td>
                <td class="pst-num">{{ count(row.systems) }}</td>
                <td v-if="fullAccess" class="pst-num">{{ money(row.revenue) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </section>
</template>

<script>
import ApiServices from '@/services/api.service'

const VIA_LABELS = { ivr: 'בטלפון', web: 'בממשק', api: 'דרך ה-API', system: 'אוטומטית (שנמוך)', unknown: 'לא ידוע' };
// סכום שלם בלי אגורות (5,488 ₪), ועם אגורות רק כשיש (59.90 ₪)
const moneyWhole = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 0 });
const moneyCents = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const countFormat = new Intl.NumberFormat('he-IL');
const dateFormat = new Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });

export default {
  name: 'PlansStatsComp',
  data: () => ({
    stats: null,
    loading: false,
    error: false,
    openKeys: [],
  }),
  computed: {
    t() {
      return this.stats.totals;
    },
    // ההכנסות מגיעות מהשרת רק למי שיש לו הרשאה מלאה
    fullAccess() {
      return this.stats.full_access === true;
    },
    headline() {
      const t = this.t;
      if (!t.systems) return 'עדיין אין מערכות.';
      if (!t.paid) return `אין עדיין מערכות בתשלום מתוך ${this.count(t.systems)} מערכות.`;
      const base = `${this.count(t.paid)} מערכות בתשלום מתוך ${this.count(t.systems)}`;
      return this.fullAccess ? `${base} מכניסות ${this.money(t.revenue_active)} בחודש` : base;
    },
    figures() {
      const list = [];
      if (this.fullAccess) {
        list.push({ key: 'revenue', label: 'הכנסה חודשית', value: this.money(this.t.revenue_active), note: 'מהוראות קבע פעילות' });
        list.push({ key: 'avg', label: 'ממוצע למערכת בתשלום', value: this.money(this.t.avg_paid), note: 'לפי מחירי החבילות' });
      } else {
        list.push({ key: 'paid', label: 'מערכות בתשלום', value: this.count(this.t.paid), note: this.pct(this.t.paid) + ' מכלל המערכות' });
      }
      list.push({ key: 'new', label: 'מערכות חדשות', value: this.count(this.newSystemsTotal), note: 'ב-30 הימים האחרונים' });
      list.push({ key: 'changes', label: 'מעברי חבילה', value: this.count(this.changesTotal), note: 'ב-30 הימים האחרונים' });
      return list;
    },
    mix() {
      const t = this.t;
      return [
        { key: 'paid', label: 'בתשלום', value: t.paid },
        { key: 'free', label: 'חינמיות', value: t.free },
        { key: 'trial', label: 'בניסיון', value: t.trial },
        { key: 'missing', label: 'על חבילה שלא קיימת', value: t.missing_plan },
      ].filter(seg => seg.value > 0);
    },
    mixLabel() {
      return this.mix.map(seg => `${seg.label}: ${seg.value}`).join(', ');
    },
    // מתי חושבו הנתונים (הם נשמרים לכמה שעות). לא מהיום - גם התאריך
    updatedAt() {
      const at = new Date(this.stats.generated_at);
      const time = at.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
      return at.toDateString() === new Date().toDateString() ? time : `${this.date(at)} ${time}`;
    },
    attention() {
      const a = this.stats.attention;
      const items = [
        {
          key: 'paid_no_hok', tone: 'risk', count: a.paid_no_hok.count, list: a.paid_no_hok.list,
          title: 'בתשלום בלי הוראת קבע',
          hint: `${this.money(this.t.revenue_at_risk)} בחודש שלא נגבים`,
          detailHeader: 'חבילה', detail: (row) => `${row.plan_name} (${this.money(row.price)})`,
        },
        {
          key: 'missing_plan', tone: 'risk', count: a.missing_plan.count, list: a.missing_plan.list,
          title: 'על חבילה שלא קיימת',
          hint: 'החבילה נמחקה - צריך לשייך חבילה אחרת',
          detailHeader: '', detail: () => '',
        },
        {
          key: 'trials_ending', tone: 'warn', count: a.trials_ending.count, list: a.trials_ending.list,
          title: 'תקופת הניסיון נגמרת בשבוע הקרוב',
          hint: 'כדאי ליצור קשר לפני שהמערכת נחסמת',
          detailHeader: 'נגמרת ב', detail: (row) => this.date(row.ends),
        },
        {
          key: 'trials_ended', tone: 'muted', count: a.trials_ended.count, list: a.trials_ended.list,
          title: 'תקופת הניסיון הסתיימה',
          hint: 'עדיין על חבילת הניסיון',
          detailHeader: 'הסתיימה ב', detail: (row) => (row.ends ? this.date(row.ends) : 'בלי תאריך'),
        },
        {
          key: 'storage_candidates', tone: 'gold', count: a.storage_candidates.count, list: a.storage_candidates.list,
          title: 'חינמיות שהאחסון שלהן כמעט מלא',
          hint: 'קיבלו התראת אחסון - מועמדות לשדרוג',
          detailHeader: 'חבילה', detail: (row) => row.plan_name,
        },
      ];
      return items.filter(item => item.count > 0);
    },
    maxPlanSystems() {
      return Math.max(1, ...this.stats.by_plan.map(row => row.systems));
    },
    newSystemsRows() {
      const n = this.stats.last_30_days.new_systems;
      return ['ivr', 'web', 'api', 'unknown'].map(key => ({ key, label: VIA_LABELS[key], value: n[key] || 0 }));
    },
    newSystemsTotal() {
      return Object.values(this.stats.last_30_days.new_systems).reduce((sum, v) => sum + v, 0);
    },
    newSystemsMax() {
      return Math.max(1, ...this.newSystemsRows.map(row => row.value));
    },
    changesRows() {
      const c = this.stats.last_30_days.plan_changes;
      return ['ivr', 'web', 'api', 'system', 'unknown']
        .map(key => ({ key, label: VIA_LABELS[key], paid: c[key].paid, free: c[key].free }))
        .filter(row => row.paid || row.free);
    },
    changesTotal() {
      return this.changesRows.reduce((sum, row) => sum + row.paid + row.free, 0);
    },
    agentRows() {
      // רק כשיש לפחות סוכן אחד עם מערכות בתשלום
      const rows = this.stats.by_agent;
      return rows.some(row => row.line_number) ? rows : [];
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = false;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(process.env.VUE_APP_BASE_URL + '/admin_api/plans_stats', ApiServices.requestOptions('GET', '', token));
        if (res.status !== 200) throw new Error(`status ${res.status}`);
        this.stats = await res.json();
      } catch (error) {
        console.error(error);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    money(value) {
      const n = Math.round((value || 0) * 100) / 100;
      return (Number.isInteger(n) ? moneyWhole : moneyCents).format(n);
    },
    count(value) {
      return countFormat.format(value || 0);
    },
    date(value) {
      return value ? dateFormat.format(new Date(value)) : '';
    },
    // אחוז מכלל המערכות. מעט מאוד (למשל 7 מתוך 4,830) - "פחות מ-1%" ולא 0% מטעה
    pct(value) {
      if (!this.t.systems || !value) return '';
      const p = (value / this.t.systems) * 100;
      return p < 1 ? 'פחות מ-1%' : `${Math.round(p)}%`;
    },
    shareWidth(systems) {
      return `${Math.max(2, Math.round((systems / this.maxPlanSystems) * 100))}%`;
    },
    barWidth(value, max) {
      return value ? `${Math.max(3, Math.round((value / max) * 100))}%` : '0%';
    },
    typeLabel(type) {
      return { paid: 'בתשלום', free: 'חינמית', trial: 'ניסיון', missing: 'לא קיימת' }[type] || type;
    },
    isOpen(key) {
      return this.openKeys.includes(key);
    },
    toggle(key) {
      this.openKeys = this.isOpen(key) ? this.openKeys.filter(k => k !== key) : [...this.openKeys, key];
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.pst {
  --ink: #0d2c6d;
  --ink-soft: #3d5488;
  --gold: #ffb91a;
  --paper: #f5f7fb;
  --line: #e3e8f2;
  --muted: #6b7690;
  --paid: #0d2c6d;
  --free: #9fb0cf;
  --trial: #ffb91a;
  --risk: #c2410c;
  --ok: #2e9e5b;
  color: var(--ink);
  font-family: 'Heebo', sans-serif;
  padding: 8px 4px 24px;
  position: relative;
}

.pst-bar { position: absolute; top: 0; inset-inline: 0; }
.pst-muted { color: var(--muted); font-size: 13px; }
.pst-num { font-variant-numeric: tabular-nums; direction: ltr; unicode-bidi: isolate; }

/* ===== הכותרת ===== */
.pst-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 20px; }
.pst-headline { margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -0.01em; line-height: 1.35; max-width: 60ch; }
.pst-risk { margin: 6px 0 0; display: flex; align-items: center; gap: 6px; color: var(--risk); font-size: 15px; font-weight: 500; }
.pst-updated { display: flex; align-items: center; gap: 4px; color: var(--muted); font-size: 13px; white-space: nowrap; }

/* ===== פס ההרכב ===== */
.pst-mix { display: flex; height: 14px; border-radius: 7px; overflow: hidden; background: var(--line); gap: 2px; }
.pst-mix__seg { flex-basis: 0; min-width: 6px; transition: flex-grow .4s ease; }
.pst-mix__seg.is-paid, .pst-dot.is-paid { background: var(--paid); }
.pst-mix__seg.is-free, .pst-dot.is-free { background: var(--free); }
.pst-mix__seg.is-trial, .pst-dot.is-trial { background: var(--trial); }
.pst-mix__seg.is-missing, .pst-dot.is-missing { background: var(--risk); }
.pst-legend { list-style: none; padding: 0; margin: 10px 0 28px; display: flex; flex-wrap: wrap; gap: 6px 22px; font-size: 14px; }
.pst-legend li { display: inline-flex; align-items: center; gap: 6px; }
.pst-legend b { font-variant-numeric: tabular-nums; }
.pst-dot { width: 10px; height: 10px; border-radius: 3px; display: inline-block; }

/* ===== מספרים מרכזיים: שורה אחת עם קווים מפרידים ===== */
.pst-figures { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); margin: 0 0 32px; border-block: 1px solid var(--line); }
.pst-fig { padding: 16px 18px; border-inline-start: 1px solid var(--line); }
.pst-fig:first-child { border-inline-start: none; padding-inline-start: 0; }
.pst-fig dt { font-size: 14px; color: var(--ink-soft); font-weight: 500; }
.pst-fig dd { margin: 4px 0 2px; font-size: 28px; font-weight: 800; font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }

/* ===== בלוקים ===== */
.pst-block { margin-bottom: 32px; }
.pst-h { font-size: 18px; font-weight: 700; margin: 0 0 12px; }
.pst-sub { margin: -8px 0 12px; }
.pst-empty { display: flex; align-items: center; gap: 6px; color: var(--ok); margin: 0; }
.pst-scroll { overflow-x: auto; }

/* ===== דורש טיפול ===== */
.pst-att { border: 1px solid var(--line); border-radius: 12px; background: #fff; margin-bottom: 8px; border-inline-start: 4px solid var(--muted); }
.pst-att.is-risk { border-inline-start-color: var(--risk); }
.pst-att.is-warn { border-inline-start-color: #e0920b; }
.pst-att.is-gold { border-inline-start-color: var(--gold); }
.pst-att__row { width: 100%; display: flex; align-items: center; gap: 16px; padding: 12px 16px; text-align: start; font: inherit; color: inherit; background: none; border: 0; cursor: pointer; }
.pst-att__row:focus-visible { outline: 2px solid var(--gold); outline-offset: -2px; border-radius: 10px; }
.pst-att__count { min-width: 48px; font-size: 24px; font-weight: 800; font-variant-numeric: tabular-nums; }
.pst-att.is-risk .pst-att__count { color: var(--risk); }
.pst-att__text { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.pst-att__text b { font-size: 15px; }
.pst-att__chev { transition: transform .2s ease; }
.pst-att__chev.is-open { transform: rotate(180deg); }
.pst-att__list { padding: 0 16px 12px; }
.pst-note { margin: 8px 0 0; }

/* ===== טבלאות ===== */
.pst-table, .pst-mini { width: 100%; border-collapse: collapse; font-size: 14px; }
.pst-table th, .pst-mini th { text-align: start; font-weight: 500; color: var(--muted); font-size: 13px; padding: 8px 12px; border-bottom: 1px solid var(--line); white-space: nowrap; }
.pst-table td, .pst-mini td { padding: 10px 12px; border-bottom: 1px solid var(--line); vertical-align: top; }
.pst-mini td { padding: 7px 12px; }
.pst-table tbody tr:last-child td, .pst-mini tbody tr:last-child td { border-bottom: none; }
.pst-plan { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.pst-plan__name { font-weight: 700; }
.pst-tag { font-size: 12px; padding: 1px 8px; border-radius: 999px; background: var(--paper); color: var(--ink-soft); border: 1px solid var(--line); }
.pst-tag.is-paid { background: rgba(13, 44, 109, .08); color: var(--ink); border-color: transparent; }
.pst-tag.is-trial { background: rgba(255, 185, 26, .18); color: #8a5a00; border-color: transparent; }
.pst-tag.is-missing { background: rgba(194, 65, 12, .1); color: var(--risk); border-color: transparent; }
.pst-share { display: flex; align-items: center; gap: 10px; min-width: 140px; }
.pst-share__track { flex: 1; height: 6px; background: var(--line); border-radius: 3px; overflow: hidden; }
.pst-share__fill { display: block; height: 100%; background: var(--free); border-radius: 3px; }
.pst-share__fill.is-paid { background: var(--paid); }
.pst-share__fill.is-trial { background: var(--trial); }
.pst-share__fill.is-missing { background: var(--risk); }
.pst-gap { display: block; color: var(--risk); font-size: 12px; direction: rtl; }

/* ===== 30 הימים האחרונים ===== */
.pst-split { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; }
.pst-bars, .pst-changes { list-style: none; padding: 0; margin: 0; display: grid; gap: 10px; }
.pst-bars li { display: grid; grid-template-columns: 110px 1fr 48px; align-items: center; gap: 10px; font-size: 14px; }
.pst-bars__label { color: var(--ink-soft); }
.pst-bars__track { height: 8px; background: var(--line); border-radius: 4px; overflow: hidden; }
.pst-bars__fill { display: block; height: 100%; background: var(--ink); border-radius: 4px; }
.pst-changes li { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; font-size: 14px; }
.pst-changes .pst-bars__label { min-width: 110px; }
.pst-chip { font-size: 13px; padding: 2px 10px; border-radius: 999px; font-variant-numeric: tabular-nums; }
.pst-chip.is-paid { background: rgba(13, 44, 109, .08); color: var(--ink); }
.pst-chip.is-free { background: var(--paper); color: var(--ink-soft); border: 1px solid var(--line); }

/* ===== שגיאה וטעינה ===== */
.pst-error { text-align: center; padding: 48px 16px; color: var(--ink-soft); }
.pst-error p { margin-bottom: 16px; }

@media (max-width: 900px) {
  .pst-figures { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
  .pst-fig:nth-child(3) { border-inline-start: none; padding-inline-start: 0; }
  .pst-fig:nth-child(n + 3) { border-top: 1px solid var(--line); }
  .pst-split { grid-template-columns: 1fr; gap: 24px; }
}

@media (max-width: 600px) {
  .pst-head { flex-direction: column; }
  .pst-headline { font-size: 20px; }
  .pst-fig dd { font-size: 22px; }
  .pst-att__row { gap: 10px; padding: 10px 12px; }
  .pst-att__count { min-width: 36px; font-size: 20px; }
}

@media (prefers-reduced-motion: reduce) {
  .pst-mix__seg, .pst-att__chev { transition: none; }
}
</style>